import { type Decimal, dec, ONE, toCents, toFixedTrimmed, truncate, ZERO } from "./decimal";
import { MoneyError } from "./money";

const HUNDRED_CENTS = dec("100");

/**
 * Spreading an invoice's freight, fees and taxes across the stock it delivered.
 *
 * The invoice always displays as printed (invariant 6); this decides what lands in unit cost. Scott
 * (via Tyler, 2026-09-20) wants it split per item: $90 of fees over an invoice of 42 units is $2.14 a
 * unit, whatever those units weigh or cost. Ollie offers by-weight as well, and by extended price is
 * what most accounting systems default to, so all three are here and a brewery picks.
 *
 * Which one is chosen matters most where breweries spend most: on CMG invoice USINV000326453 the two
 * hop lines take $1.88 of the $94.90 freight by weight, $8.44 by unit count and $27.78 by extended
 * price, because hops are the light, expensive thing on every malt order.
 *
 * All three methods are proportional, so allocating to lines and then splitting each line across its
 * lots gives the same shares as allocating straight to lots. Pass whichever rows need an amount; only
 * where the remainder pennies land changes.
 */

export const ALLOCATION_METHODS = ["unit_count", "weight", "extended_price"] as const;

export type AllocationMethod = (typeof ALLOCATION_METHODS)[number];

/**
 * White Oak's preference, and so the default until defaults are configurable per brewery under
 * Settings (Tyler, 2026-09-20). Stored on the invoice at commit, never read from settings afterwards:
 * changing the default later must not restate what historical lots cost.
 */
export const DEFAULT_ALLOCATION_METHOD: AllocationMethod = "unit_count";

export interface AllocatableLine {
  /** The caller's own key, returned unchanged. */
  id: string;
  /** Packs or each, as the invoice counts them: 13 bags, 2 cartons. */
  units: string | Decimal;
  /** Canonical mass of the whole line. Null for stock counted without one, such as kegs. */
  weight?: string | Decimal | null;
  /** The line's extended price, as the invoice prints it. */
  extendedPrice: string | Decimal;
  /**
   * False for lines that deliver no stock: pallet and keg deposits, credits, returns. They sit out of
   * the base, so one deposit line cannot change what every other line absorbs. Defaults to true.
   */
  receivesStock?: boolean;
}

export interface CostAllocation {
  id: string;
  /** This line's share, exact to the cent. Zero for lines that deliver no stock. */
  amount: string;
}

export class AllocationError extends MoneyError {}

/**
 * Splits `total` across `lines`, exactly. The returned amounts always sum to `total` to the cent: the
 * shares are taken in whole cents and the remainder is handed out largest fraction first, ties by the
 * order the lines came in, so the same invoice always allocates the same way.
 *
 * Throws when no line can carry the cost, which is a real condition a brewer can fix by choosing
 * another method: an invoice of kegs has no weight to divide by.
 */
export function allocateCosts(
  total: string | Decimal,
  lines: readonly AllocatableLine[],
  method: AllocationMethod = DEFAULT_ALLOCATION_METHOD,
): CostAllocation[] {
  const cents = toCents(dec(total));
  const bases = lines.map((line) => (line.receivesStock === false ? ZERO : basisOf(line, method)));

  for (const basis of bases) {
    if (basis.lt(ZERO)) throw new AllocationError("A line cannot carry a negative share of costs");
  }

  const basisTotal = bases.reduce((running, basis) => running.plus(basis), ZERO);
  if (cents.eq(ZERO)) return lines.map((line) => ({ id: line.id, amount: "0" }));
  if (basisTotal.lte(ZERO)) {
    throw new AllocationError(
      `No line on this invoice has a ${describe(method)} to divide costs by`,
    );
  }

  // Work in whole cents of the absolute total so the remainder rule behaves the same on a discount.
  const sign = cents.lt(ZERO) ? dec("-1") : ONE;
  const magnitude = cents.abs();

  const exact = bases.map((basis) => magnitude.times(basis).div(basisTotal));
  const whole = exact.map(truncate);
  const allotted = whole.reduce((running, share) => running.plus(share), ZERO);

  // `exact` sums to `magnitude`, so truncating leaves fewer than `lines.length` cents over.
  let remainder = Number(magnitude.minus(allotted).toFixed());
  const byFraction = exact
    .map((share, index) => ({ index, fraction: share.minus(whole[index] as Decimal) }))
    .sort((a, b) => b.fraction.cmp(a.fraction) || a.index - b.index);

  for (const { index } of byFraction) {
    if (remainder <= 0) break;
    whole[index] = (whole[index] as Decimal).plus(ONE);
    remainder -= 1;
  }

  return lines.map((line, index) => ({
    id: line.id,
    amount: toFixedTrimmed((whole[index] as Decimal).times(sign).div(HUNDRED_CENTS), 2),
  }));
}

function basisOf(line: AllocatableLine, method: AllocationMethod): Decimal {
  switch (method) {
    case "unit_count":
      return dec(line.units);
    case "weight":
      // A line with no weight carries no freight under a method that divides by weight. Kegs and
      // other counted stock on a mixed invoice fall out this way rather than failing the whole thing.
      return line.weight == null ? ZERO : dec(line.weight);
    case "extended_price":
      return dec(line.extendedPrice);
  }
}

function describe(method: AllocationMethod): string {
  switch (method) {
    case "unit_count":
      return "unit count";
    case "weight":
      return "weight";
    case "extended_price":
      return "price";
  }
}
